import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Download,
  RefreshCw,
  Shield,
  User,
  Building,
  Ticket,
  Mail,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { qrAPI } from "../services/api";
import "./MyQRCode.css";

const MyQRCode = () => {
  const { user } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchQRCode();
  }, []);

  const fetchQRCode = async () => {
    try {
      const response = await qrAPI.getMyQR();
      setQrData(response.data);
    } catch (error) {
      console.error("Failed to fetch QR:", error);
      // Fallback for demo
      setQrData({
        code: `RF2025-${user?.id || "DEMO"}-${Date.now()}`,
        attendee_name: user?.name || "Demo Attendee",
        registration_id: user?.registration_id || "REG-2025-001",
        organization: user?.organization || "Demo Organization",
        pass_type: "Full Access",
        valid_until: "2025-01-22T23:59:59",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchQRCode();
    setRefreshing(false);
  };

  const handleDownload = () => {
    const svg = document.getElementById("qr-code-svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const logoImg = new Image();
    const qrImg = new Image();
    const avatarImg = new Image();

    const loadLogo = new Promise((resolve) => {
      logoImg.crossOrigin = "anonymous";
      logoImg.onload = () => resolve(true);
      logoImg.onerror = () => resolve(false);
      logoImg.src = "/logo.png";
    });

    const loadQR = new Promise((resolve) => {
      qrImg.onload = () => resolve(true);
      qrImg.src = "data:image/svg+xml;base64," + btoa(svgData);
    });

    const loadAvatar = new Promise((resolve) => {
      if (!user?.avatar_url) {
        resolve(false);
        return;
      }
      avatarImg.crossOrigin = "anonymous";
      avatarImg.onload = () => resolve(true);
      avatarImg.onerror = () => resolve(false);
      avatarImg.src = user.avatar_url;
    });

    Promise.all([loadLogo, loadQR, loadAvatar]).then(
      ([logoLoaded, qrLoaded, avatarLoaded]) => {
        console.log("Download debug:", {
          user,
          qrData,
          logoLoaded,
          avatarLoaded,
        });

        // Set canvas dimensions
        canvas.width = 400;
        // Calculate height based on content
        canvas.height = avatarLoaded ? 680 : 550;

        // Fill white background
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw Header Text
        ctx.font = "bold 24px sans-serif";
        ctx.fillStyle = "#003366";
        ctx.textAlign = "center";
        ctx.fillText("Conference Pass", 200, 50);

        // Draw QR Code
        ctx.drawImage(qrImg, 50, 70, 300, 300);

        // Draw Logo centered over QR
        if (logoLoaded) {
          ctx.drawImage(logoImg, 170, 190, 60, 60);
        }

        let currentY = 400;

        // Draw Avatar if loaded
        if (avatarLoaded) {
          ctx.save();
          ctx.beginPath();
          // Circle center at (200, 440), radius 40
          ctx.arc(200, 440, 40, 0, Math.PI * 2, true);
          ctx.closePath();
          ctx.clip();
          // Draw image centered in circle
          ctx.drawImage(avatarImg, 160, 400, 80, 80);
          ctx.restore();

          // Draw border around avatar
          ctx.beginPath();
          ctx.arc(200, 440, 40, 0, Math.PI * 2, true);
          ctx.lineWidth = 2;
          ctx.strokeStyle = "#E2E8F0";
          ctx.stroke();

          currentY = 510;
        } else {
          currentY = 410;
        }

        // Draw Name
        const nameToDraw =
          qrData.attendee_name ||
          qrData.full_name ||
          user.full_name ||
          user.name ||
          "Attendee";
        ctx.fillStyle = "#1e293b";
        ctx.font = "bold 22px Inter, sans-serif";
        ctx.fillText(nameToDraw, 200, currentY);

        // Draw Email
        ctx.fillStyle = "#64748b";
        ctx.font = "16px Inter, sans-serif";
        ctx.fillText(user.email, 200, currentY + 30);

        // Draw Registration ID
        ctx.fillStyle = "#003366";
        ctx.font = "bold 16px monospace";
        ctx.fillText(qrData.registration_id, 200, currentY + 60);

        // Draw Organization if exists
        if (qrData.organization) {
          ctx.fillStyle = "#64748b";
          ctx.font = "14px Inter, sans-serif";
          ctx.fillText(qrData.organization, 200, currentY + 85);
        }

        // Draw Footer
        ctx.fillStyle = "#94a3b8";
        ctx.font = "12px sans-serif";
        ctx.fillText("RF Conference 2026", 200, canvas.height - 20);

        try {
          const pngUrl = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.download = `RF-Conference-QR-${qrData.registration_id}.png`;
          link.href = pngUrl;
          link.click();
        } catch (err) {
          console.error("Error generating image:", err);
          alert(
            "Could not download image, possibly due to browser security restrictions on the profile image.",
          );
        }
      },
    );
  };

  if (loading) {
    return (
      <div className="qr-page">
        <div className="qr-skeleton">
          <div className="skeleton qr-placeholder" />
          <div
            className="skeleton"
            style={{ height: 24, width: "60%", margin: "16px auto" }}
          />
          <div
            className="skeleton"
            style={{ height: 16, width: "40%", margin: "0 auto" }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="qr-page">
      <div className="qr-container">
        <div className="qr-header">
          <Shield size={20} />
          <span>Your Event Pass</span>
        </div>

        <div className="qr-code-wrapper">
          <QRCodeSVG
            id="qr-code-svg"
            value={
              qrData
                ? `${window.location.origin}/verify/${qrData.registration_id}`
                : ""
            }
            size={220}
            level="H"
            includeMargin={true}
            bgColor="#FFFFFF"
            fgColor="#003366"
            imageSettings={{
              src: "/logo.png",
              x: undefined,
              y: undefined,
              height: 40,
              width: 40,
              excavate: true,
            }}
          />
        </div>

        <div className="attendee-info">
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="qr-profile-photo"
            />
          ) : (
            <div className="qr-profile-placeholder">
              <User size={32} />
            </div>
          )}
          <p className="pass-type">{qrData.pass_type}</p>
        </div>

        <div className="qr-details">
          <div className="detail-row">
            <User size={16} />
            <span className="label">Name</span>
            <span className="value">
              {qrData.attendee_name ||
                qrData.full_name ||
                user.full_name ||
                user.name ||
                "Attendee"}
            </span>
          </div>

          <div className="detail-row">
            <Mail size={16} />
            <span className="label">Email</span>
            <span className="value">{user.email}</span>
          </div>

          <div className="detail-row">
            <Ticket size={16} />
            <span className="label">Registration ID</span>
            <span className="value">{qrData.registration_id}</span>
          </div>
          {qrData.organization && (
            <div className="detail-row">
              <Building size={16} />
              <span className="label">Organization</span>
              <span className="value">{qrData.organization}</span>
            </div>
          )}
        </div>

        <div className="qr-actions">
          <button
            className="action-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? "spin" : ""} />
            <span>Refresh</span>
          </button>
          <button className="action-btn" onClick={handleDownload}>
            <Download size={18} />
            <span>Save</span>
          </button>
        </div>
      </div>

      <div className="qr-instructions">
        <h3>How to use your QR Code</h3>
        <ul>
          <li>Show this code at the registration desk for badge collection</li>
          <li>Scan at session entrances for attendance tracking</li>
          <li>Use for masterclass check-in</li>
          <li>Save offline for areas with poor connectivity</li>
        </ul>
      </div>

      <div className="offline-notice">
        <p>💡 This QR code is cached for offline use</p>
      </div>
    </div>
  );
};

export default MyQRCode;
